import { createAction } from 'redux-api-middleware'
import { HUBBLE_API_TOKEN, HUBBLE_MAINTENANCE_TOKEN } from '../../store/middleware/APIUrl'
import { IDUtils } from '../../utils'

const Types = {
    API_CALL_FAILURE: 'API_CALL##FAILURE',
    API_CALL_OPTIONAL_FAILURE: 'API_CALL_OPTIONAL##FAILURE',
    ATTACH_CHANNEL_DEVICE_REQUEST: 'ATTACH_CHANNEL_DEVICE##REQUEST',
    ATTACH_CHANNEL_DEVICE_SUCCESS: 'ATTACH_CHANNEL_DEVICE##SUCCESS',
    ATTACH_MEDIA_CHANNEL_REQUEST: 'ATTACH_MEDIA_CHANNEL##REQUEST',
    ATTACH_MEDIA_CHANNEL_SUCCESS: 'ATTACH_MEDIA_CHANNEL##SUCCESS',
    CHANGE_CHANNELS: 'CHANGE_CHANNELS',
    CHANGE_SELECTED_CHANNEL: 'CHANGE_SELECTED_CHANNEL',
    CREATE_CHANNEL_CP_REQUEST: 'CREATE_CHANNEL_CP##REQUEST',
    CREATE_CHANNEL_CP_SUCCESS: 'CREATE_CHANNEL_CP##SUCCESS',
    CREATE_CHANNEL_REQUEST: 'CREATE_CHANNEL##REQUEST',
    CREATE_CHANNEL_SUCCESS: 'CREATE_CHANNEL##SUCCESS',
    DELETE_CHANNEL_REQUEST: 'DELETE_CHANNEL##REQUEST',
    DELETE_CHANNEL_SUCCESS: 'DELETE_CHANNEL##SUCCESS',
    DESELECT_CHANNEL: 'DESELECT_CHANNEL',
    DESTROY_CHANNEL_FAILURE: 'DESTROY_CHANNEL##FAILURE',
    DESTROY_CHANNEL_REQUEST: 'DESTROY_CHANNEL##REQUEST',
    DESTROY_CHANNEL_SUCCESS: 'DESTROY_CHANNEL##SUCCESS',
    DETACH_CHANNEL_DEVICE_REQUEST: 'DETACH_CHANNEL_DEVICE##REQUEST',
    DETACH_CHANNEL_DEVICE_SUCCESS: 'DETACH_CHANNEL_DEVICE##SUCCESS',
    FETCH_CHANNEL_DEVICES_REQUEST: 'FETCH_CHANNEL_DEVICES##REQUEST',
    FETCH_CHANNEL_DEVICES_SUCCESS: 'FETCH_CHANNEL_DEVICES##SUCCESS',
    FETCH_CHANNEL_REQUEST: 'FETCH_CHANNEL##REQUEST',
    FETCH_CHANNEL_SUCCESS: 'FETCH_CHANNEL##SUCCESS',
    FETCH_CHANNELS_REQUEST: 'FETCH_CHANNELS##REQUEST',
    FETCH_CHANNELS_SUCCESS: 'FETCH_CHANNELS##SUCCESS',
    UPDATE_CHANNEL_REQUEST: 'UPDATE_CHANNEL##REQUEST',
    UPDATE_CHANNEL_SUCCESS: 'UPDATE_CHANNEL##SUCCESS',
    DETACH_MEDIA_CHANNEL_REQUEST: 'DETACH_MEDIA_CHANNEL##REQUEST',
    DETACH_MEDIA_CHANNEL_SUCCESS: 'DETACH_MEDIA_CHANNEL##SUCCESS',
    RELOAD_PROMO_IN_CHANNEL: 'RELOAD_PROMO_IN_CHANNEL',
    RELOAD_PROMO_IN_CHANNEL_SUCCESS: 'RELOAD_PROMO_IN_CHANNEL##SUCCESS',
}

/**
 * Fetch channels data
 * @param {number|null} [page] 
 * @param {object} [opts] 
 * @param {number[]} [opts.chains]
 * @param {string} [opts.name]
 * @returns 
 */
function fetchChannels(page, { chains, name } = {}) {
    const params = {}

    if (page > 0) {
        params.page = page
    }

    if (chains) {
        params.ch = chains
    }

    if (name) {
        params.name = name
    }

    const query = new URLSearchParams(params).toString()

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels?${query}`,
        method: 'GET',
        types: [
            Types.FETCH_CHANNELS_REQUEST,
            {
                type: Types.FETCH_CHANNELS_SUCCESS,
                meta: { noFirstPage: page > 1 },
            },
            Types.API_CALL_FAILURE,
        ],
        options: {
            abortable: true,
        },
    })
}

function fetchChannel(channelID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}`,
        method: 'GET',
        types: [Types.FETCH_CHANNEL_REQUEST, Types.FETCH_CHANNEL_SUCCESS, Types.API_CALL_FAILURE],
    })
}

/**
 * Deselects the selected channel
 */
function deselectChannel() {
    return {
        type: Types.DESELECT_CHANNEL,
    }
}

/**
 * Store new channel
 * @param {Number} id
 * @param {Object} channel
 */
function createChannel(channel) {
    const { playlists: playlistsRaw = [] } = channel || {}

    // Transform playlists array into an array of playlist ids
    const playlists = playlistsRaw.map(({ id: playlistID }) => playlistID)
    const data = {
        ...channel,
        playlists,
    }

    const body = JSON.stringify(data)

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels`,
        method: 'POST',
        types: [Types.CREATE_CHANNEL_REQUEST, Types.CREATE_CHANNEL_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

/**
 * Update channel record
 * @param {Number} id
 * @param {Object} channel
 */
function updateChannel(id, channel) {
    const { playlists: playlistsRaw, media: mediaRaw } = channel || {}

    let playlists = null
    let media = null
    if (playlistsRaw) {
        // Transform playlists array into an array of playlist ids
        playlists = playlistsRaw.map(({ id: playlistID }) => playlistID)
    }

    // Transform media array into an array with the form [media-id, order]
    if (mediaRaw) media = mediaRaw.map(({ id: mediaID }, index) => [mediaID, index]).reduce((a, b) => [...a, b], [])

    const data = {
        ...channel,
        playlists,
        media,
    }

    const body = JSON.stringify(data)

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${id}`,
        method: 'PUT',
        types: [
            Types.UPDATE_CHANNEL_REQUEST,
            {
                type: Types.UPDATE_CHANNEL_SUCCESS,
                meta: { withArray: !!playlists || !!media, id },
            },
            Types.API_CALL_FAILURE,
        ],
        body,
    })
}

/**
 * Update channel record
 * @param {Number} id
 * @param {Object} channel
 */
function attachMediaToChannel(channelID, mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}/media/${mediaID}`,
        method: 'PUT',
        types: [Types.ATTACH_MEDIA_CHANNEL_REQUEST, Types.ATTACH_MEDIA_CHANNEL_SUCCESS, Types.API_CALL_FAILURE],
    })
}

function createChannelCP(name) {
    const body = JSON.stringify({
        name: name || IDUtils.generateID(),
        status: 1,
        synced: 1,
    })

    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels`,
        method: 'POST',
        types: [Types.CREATE_CHANNEL_CP_REQUEST, Types.CREATE_CHANNEL_CP_SUCCESS, Types.API_CALL_FAILURE],
        body,
    })
}

function destroyChannelCP() {
    return (dispatch, getState) => {
        const state = getState()
        const { content } = state || {}
        const { contentPushContent } = content || {}
        const { channel } = contentPushContent || {}
        const { id } = channel || {}

        if (!id) {
            return null
        }

        return dispatch(
            createAction({
                endpoint: `${HUBBLE_API_TOKEN}/channels/${id}`,
                method: 'DELETE',
                types: [Types.DESTROY_CHANNEL_REQUEST, Types.DESTROY_CHANNEL_SUCCESS, Types.DESTROY_CHANNEL_FAILURE],
            })
        )
    }
}

/**
 *
 * @param {number} channelID
 * @param {string} deviceID
 * @param {object} [opts]
 * @param {string} [opts.url]
 * @returns
 */
function attachChannelToDevice(channelID, deviceID, opts = {}) {
    const { url = HUBBLE_API_TOKEN } = opts

    // If calling maintenance namespace, use its specific path
    const endpoint = url === HUBBLE_MAINTENANCE_TOKEN
        ? [url, 'devices', deviceID, 'channel', channelID].join('/')
        : [url, 'devices', deviceID, channelID].join('/')

    return createAction({
        endpoint,
        method: 'PUT',
        types: [Types.ATTACH_CHANNEL_DEVICE_REQUEST, Types.ATTACH_CHANNEL_DEVICE_SUCCESS, Types.API_CALL_FAILURE],
    })
}

function detachChannelToDevice(channelID, deviceID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/devices/${deviceID}/${channelID}`,
        method: 'DELETE',
        types: [Types.DETACH_CHANNEL_DEVICE_REQUEST, Types.DETACH_CHANNEL_DEVICE_SUCCESS, Types.API_CALL_FAILURE],
    })
}

function fetchChannelDevices(channelID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}/devices`,
        method: 'GET',
        types: [Types.FETCH_CHANNEL_DEVICES_REQUEST, Types.FETCH_CHANNEL_DEVICES_SUCCESS, Types.API_CALL_OPTIONAL_FAILURE],
    })
}

/**
 * Fetch channel information with the list of the devices attached to it
 * @param {Number} channelID ID of the channel to fetch
 */
function fetchChannelWithDevices(channelID) {
    return async (dispatch) => {
        await dispatch(fetchChannel(channelID))
        await dispatch(fetchChannelDevices(channelID))
    }
}

function deleteChannel(channelID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}`,
        method: 'DELETE',
        types: [
            Types.DELETE_CHANNEL_REQUEST,
            {
                type: Types.DELETE_CHANNEL_SUCCESS,
                meta: { id: channelID },
            },
            Types.API_CALL_FAILURE,
        ],
    })
}

function changeChannelsArray(channels) {
    return {
        type: Types.CHANGE_CHANNELS,
        payload: channels,
    }
}

function changeSelectedChannel(channel) {
    return {
        type: Types.CHANGE_SELECTED_CHANNEL,
        payload: channel,
    }
}

/**
 * Detach media from a channel
 * @param {Number} id
 * @param {Object} channel
 */
function detachMediaFromChannel(channelID, mediaID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}/media/${mediaID}`,
        method: 'DELETE',
        types: [
            Types.DETACH_MEDIA_CHANNEL_REQUEST,
            {
                type: Types.DETACH_MEDIA_CHANNEL_SUCCESS,
                meta: { id: mediaID },
            },
            Types.API_CALL_FAILURE,
        ],
    })
}

function reloadPromoInChannel(channelID) {
    return createAction({
        endpoint: `${HUBBLE_API_TOKEN}/channels/${channelID}/reload-promo`,
        method: 'POST',
        types: [Types.RELOAD_PROMO_IN_CHANNEL, Types.RELOAD_PROMO_IN_CHANNEL_SUCCESS, Types.API_CALL_OPTIONAL_FAILURE],
    })
}

export {
    Types,
    attachChannelToDevice,
    changeChannelsArray,
    changeSelectedChannel,
    createChannel,
    createChannelCP,
    deleteChannel,
    deselectChannel,
    destroyChannelCP,
    detachChannelToDevice,
    fetchChannel,
    fetchChannelWithDevices,
    fetchChannels,
    updateChannel,
    attachMediaToChannel,
    detachMediaFromChannel,
    reloadPromoInChannel,
}
